import { z } from 'zod';
import { FeedEventSchema, PROTOCOL_VERSION } from './events.js';
/**
 * Wire envelope for every agent -> server message.
 *
 * `seq` is per-session and strictly monotonic, so the server can detect gaps and
 * discard duplicates when the agent replays its outbox after a reconnect.
 *
 * `at` is the agent's wall clock. The server records its own `receivedAt` on
 * ingest and the delay buffer keys off THAT — agent clock skew must never be
 * able to shift how far behind the viewer feed runs.
 */
export const FeedEnvelopeSchema = z.object({
    v: z.literal(PROTOCOL_VERSION),
    seq: z.number().int().nonnegative(),
    sessionId: z.string().min(1),
    at: z.number().int().nonnegative(),
    event: FeedEventSchema,
});
/**
 * Transport frames, agent -> server.
 *
 * The `hello` frame is connection-level, not session-level: it carries what the
 * agent needs to resume after a dropped socket (its high-water seq), which is
 * per-connection information and does not belong in the recorded event stream.
 */
export const AgentHelloFrameSchema = z.object({
    kind: z.literal('hello'),
    protocolVersion: z.literal(PROTOCOL_VERSION),
    sessionId: z.string().min(1),
    agentVersion: z.string(),
    machine: z.string(),
    /** Original session id when this is a replay; null for live play. */
    replayOf: z.string().nullable().default(null),
    /** Highest seq the agent has produced so far. */
    lastSeq: z.number().int().nonnegative(),
});
export const AgentEnvelopeFrameSchema = z.object({
    kind: z.literal('env'),
    envelope: FeedEnvelopeSchema,
});
export const AgentToServerSchema = z.discriminatedUnion('kind', [
    AgentHelloFrameSchema,
    AgentEnvelopeFrameSchema,
]);
/** Server -> agent. The agent never takes orders from the server beyond these. */
export const ServerToAgentSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('ack'),
        /** Highest contiguous seq the server has durably stored. */
        seq: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('hello_ok'),
        sessionId: z.string(),
        /** Server-known high-water mark, so the agent can trim its outbox. */
        lastSeq: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('error'),
        message: z.string(),
        fatal: z.boolean().default(false),
    }),
]);
//# sourceMappingURL=envelope.js.map